import { AfterContentInit, ChangeDetectorRef, Component, inject, input, OnInit, Optional, output } from "@angular/core";
import { Router } from "@angular/router";
import { toPromise } from "@app/_helpers/promise";
import { ClockService } from "@app/_services/clock.service";
import { AppService } from "@app/app.service";
import { ProjectPageService } from "@app/pages/settings/projects/project-page/project-page.service";
import { RecordToolbarService } from "@app/shared/record-toolbar/record-toolbar.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
	Logger,
	MyTimesQuery,
	Project,
	ProjectsQuery,
	ProjectsService,
	Time,
	UserService,
	UserSettingsQuery,
} from "@timeghost/web-api";
import { differenceInSeconds, roundToNearestMinutes } from "date-fns/esm";
import { combineLatest } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";

const log = new Logger("RecordWidgetComponent");
@UntilDestroy()
@Component({
	selector: "tg-record-widget",
	templateUrl: "./record-widget.component.html",
	styleUrls: ["./record-widget.component.scss"],
})
export class RecordWidgetComponent implements OnInit, AfterContentInit {
	private readonly clock = inject(ClockService);
	readonly time$ = this.recordService.runningTime.asObservable();

	readonly switchEnabled = input(null, { alias: "switch", transform: (v) => v === false || true });
	readonly onSwitch = output();
	readonly now$ = this.clock.now$.pipe(map((now) => roundToNearestMinutes(now, { nearestTo: 1 })));
	readonly timeRunning$ = combineLatest([this.time$, this.clock.now$]).pipe(
		untilDestroyed(this),
		distinctUntilChanged(([prevTime], [time]) => (!!time || (prevTime && !prevTime.end) ? false : true)),
		map(([time, now]) => (time ? { ...time, now } : time))
	);
	readonly stopDisabled$ = this.timeRunning$.pipe(
		map((x) => {
			if (!x) return false;
			const start = new Date(x.start);
			const now = roundToNearestMinutes(new Date(), { nearestTo: 1 });
			return differenceInSeconds(now, start) < 60;
		})
	);
	constructor(
		private myTimeQuery: MyTimesQuery,
		private router: Router,
		@Optional()
		private projectPage: ProjectPageService,
		private recordService: RecordToolbarService,
		private projectService: ProjectsService,
		private projectsQuery: ProjectsQuery,
		private user: UserSettingsQuery,
		private userService: UserService,
		private cdr: ChangeDetectorRef,
		private appService: AppService
	) {}
	private _isLoading = false;
	get isLoading() {
		return this._isLoading || this.recordService.isLoading;
	}
	set isLoading(val: boolean) {
		this._isLoading = val;
	}
	hasTimeRunning = false;
	ngOnInit(): void {
		this.time$.pipe(untilDestroyed(this)).subscribe((x) => (this.hasTimeRunning = !!x));
	}
	ngAfterContentInit() {}
	get canAdd() {
		const project = this.projectPage?.project;
		if (!project) return true;
		return !project.completed && project.projectType !== "template";
	}
	readonly showWidget = this.user.select().pipe(
		map((user) => {
			return !(user.workspace?.settings?.requireProject || user.workspace?.settings?.requireTask);
		})
	);
	readonly projecthoursEnabled$ = this.appService.projecthoursEnabled$;
	async openCreateDialog() {
		let project: Project,
			task = this.projectPage?.selectedTask.value;
		if (
			this.projectPage?.projectId &&
			(project =
				this.projectsQuery.getEntity(this.projectPage.projectId) ??
				(await this.projectService.getById(this.projectPage.projectId).catch((): null => null)))
		)
			return this.recordService.openCreateDialog({ project, task });

		const value = this.recordService.group.value;
		return this.recordService.openCreateDialog({
			...value,
		});
	}
	openUpdateDialog(time: Time, forceUpdate: boolean = false) {
		return this.recordService.openUpdateDialog(time, forceUpdate);
	}
	async startRecord() {
		this.isLoading = true;
		let project: Project,
			task = this.projectPage?.selectedTask.value;
		if ((this.user.getValue().settings?.tableDisplay as any) === "ComeGo")
			await toPromise(
				this.userService.changeSettings({
					tableDisplay: "Simple",
				})
			);
		if (
			this.projectPage?.projectId &&
			(project =
				this.projectsQuery.getEntity(this.projectPage.projectId) ??
				(await this.projectService.getById(this.projectPage.projectId).catch((): null => null)))
		)
			return await this.recordService.startRecord({ project, task }).finally(() => (this.isLoading = false));
		const { name, project: rproject, task: rtask } = this.recordService.group.value;
		return await this.recordService
			.startRecord({ project: rproject, task: rtask, name })
			.finally(() => this.resetLoading());
	}
	async stopRecord() {
		this.isLoading = true;
		const requiresUpdateDialog = !this.areRequiredFieldsSatisfied();
		return await this.recordService
			.stopRecord(
				requiresUpdateDialog
					? { requireUpdate: true, checkServiceControls: true }
					: { showUpdate: false, checkServiceControls: true }
			)
			.finally(() => this.resetLoading())
			.then((x) => {
				if (x?.length) this.recordService.resetEntites();
			});
	}

	private areRequiredFieldsSatisfied(): boolean {
		const user = this.user.getValue();
		const ws = user?.workspace?.settings;
		const value = this.recordService.group.value;

		const requireName = !!ws?.requireName;
		const requireProject = !!ws?.requireProject;
		const requireTask = !!ws?.requireTask;

		if (requireName) {
			const name = `${value?.name ?? ""}`.trim();
			if (name.length < 3) return false;
		}
		if (requireProject) {
			if (!value?.project?.id || !this.projectsQuery.getEntity(value.project.id)?.useAsDefault) return false;
		}
		if (requireTask) {
			if (!value?.task?.id) return false;
		}
		return true;
	}
	async delete(id: string) {
		this.isLoading = true;
		return await this.recordService.discardRunningRecord(id).finally(() => {
			this.resetLoading();
			this.recordService.notifySuccess("success.recordingDiscarded");
		});
	}
	resetLoading() {
		this.isLoading = false;
	}
}
